import { useState, useEffect, useRef } from "react";

// ─── THEME ────────────────────────────────────────────────────────────────────
const EL_COLORS = ["#2563eb","#0891b2","#9333ea","#16a34a","#d97706","#dc2626","#7c3aed","#0d9488","#ea580c"];

const ELEMENTS = [
  { id:1, label:"Element 1", title:"KYC & Suitability",                   icon:"👤", target:27 },
  { id:2, label:"Element 2", title:"Fixed Income",                         icon:"💵", target:10 },
  { id:3, label:"Element 3", title:"Equities",                             icon:"📈", target:12 },
  { id:4, label:"Element 4", title:"Securities Analysis",                  icon:"🔍", target:14 },
  { id:5, label:"Element 5", title:"Managed Products & Other Investments", icon:"🗂️", target:16 },
  { id:6, label:"Element 6", title:"Portfolio Construction",               icon:"⚖️", target:15 },
  { id:7, label:"Element 7", title:"Investment Recommendations",           icon:"✅", target:14 },
  { id:8, label:"Element 8", title:"Execution & Market Integrity",         icon:"🏛️", target:7  },
  { id:9, label:"Element 9", title:"Monitoring & Reporting",               icon:"📊", target:5  },
];

// shuffle(raw, pos) → puts raw[0] (correct) at index pos; fills rest with raw[1..3]
function shuffle(raw, pos) {
  const wrongs = [raw[1], raw[2], raw[3]];
  const opts = [...wrongs];
  opts.splice(pos, 0, raw[0]);
  return { opts, correct: pos };
}

// ─── RAW QUESTIONS ────────────────────────────────────────────────────────────
// raw[0] is ALWAYS the correct answer; pos = where to place it (0=A,1=B,2=C,3=D)
// Distribution: A×28, B×31, C×30, D×31
const RAW = [
// ══ ELEMENT 1 — Q1-27 ══
{ id:1,  el:1, pos:1, q:"A prospective client is eager to start trading but refuses to provide information regarding their annual income and net worth. What is the RR's only compliant action?",
  raw:["The RR must refuse to open the account, as KYC information is a regulatory requirement necessary to perform a suitability determination.",
       "The RR can open the account but must mark it as 'unsolicited trades only.'",
       "The RR can open the account as long as the client signs a waiver taking full responsibility for any losses.",
       "The RR can open the account but must limit the client to buying GICs and Money Market funds."],
  exp:"Under CIRO rules and CFRs, an RR cannot fulfill their suitability obligation without a complete KYC profile. If a client refuses to provide essential financial data, the account cannot be opened." },

{ id:2,  el:1, pos:2, q:"An RR is meeting with a 75-year-old client who appears confused. What is the primary purpose of the Trusted Contact Person (TCP) in this scenario?",
  raw:["The TCP acts as a resource for the RR to discuss concerns about the client's well-being or suspected financial exploitation without granting the TCP trading authority.",
       "The TCP is a co-owner of the account and can place trades on the client's behalf during emergencies.",
       "The TCP is a secondary beneficiary who will inherit the account assets upon the client's death.",
       "The TCP is a legal Power of Attorney (POA) who must sign off on all high-risk trades."],
  exp:"A TCP is a 'protective' contact. They do not have the power to trade or make decisions, but give the RR a safe person to contact if the RR suspects the client is being scammed or losing capacity to manage their affairs." },

{ id:3,  el:1, pos:3, q:"During a market downturn, a client with a 'Medium Risk' tolerance calls expressing extreme anxiety and a desire to sell everything. How should the RR address this?",
  raw:["The RR must review the client's KYC profile to determine if their 'Risk Aversion' is lower than previously recorded and update the KYC if necessary.",
       "The RR should immediately place the trades to cash, as the client's current emotional state overrides the long-term KYC plan.",
       "The RR should ignore the call and wait for the market to recover before speaking to the client.",
       "The RR should recommend high-risk options to try and 'make back' the recent losses quickly."],
  exp:"Suitability is not just about financial math; it's about 'Willingness to take risk.' If a client panics during a normal market fluctuation, their recorded risk tolerance may be inaccurately high and must be reassessed." },

{ id:4,  el:1, pos:1, q:"A client wants to save for a child's education in 15 years. They have high income but very little investment knowledge. Which KYC factor is most 'flexible' in allowing for a higher-risk equity recommendation?",
  raw:["The 15-year Time Horizon, which acts as a 'shock absorber' for short-term market volatility.",
       "The client's lack of investment knowledge, which means they will trust the RR more.",
       "The client's high income, which allows them to ignore all losses.",
       "The child's age, which is not a factor in KYC."],
  exp:"A long time horizon is the most critical factor for justifying risk. It allows the portfolio to recover from inevitable 'down' years before the money is actually needed." },

{ id:5,  el:1, pos:0, q:"Under the Client Focused Reforms (CFRs), which best describes the 'Client Interest First' standard?",
  raw:["When determining suitability, the RR must put the client's interest before their own and before the interests of the Dealer Member.",
       "The RR must always choose the product with the lowest possible fee, regardless of performance.",
       "The RR must ensure the client makes a profit on every single trade placed in the account.",
       "The RR must only recommend products produced 'in-house' by their own firm."],
  exp:"The 'Client Interest First' standard is the core of modern suitability. It means the RR cannot let their own commissions or the firm's profits influence the advice given to the client." },

{ id:6,  el:1, pos:2, q:"When must KYC information be updated at a minimum?",
  raw:["At least once every 12 months, or whenever a 'significant life event' occurs.",
       "Every 5 years, unless the client requests a change sooner.",
       "Only when the client wants to buy a new high-risk product.",
       "Whenever the firm's compliance department sends a general reminder."],
  exp:"CIRO rules require KYC to be updated at least annually or immediately following significant changes (e.g., marriage, inheritance, job change)." },

{ id:7,  el:1, pos:3, q:"A client insists on placing a trade to buy a 'Triple Leveraged Inverse ETF,' which the RR has determined is 'Unsuitable' for the client's Conservative risk profile. What is the RR's obligation?",
  raw:["The RR must warn the client that the trade is unsuitable, record the warning and the client's instructions, and then may proceed with the trade as 'unsolicited.'",
       "The RR must refuse the trade entirely, as an RR can never place an unsuitable trade.",
       "The RR can place the trade without warning as long as the client has the cash to pay for it.",
       "The RR should place the trade and then change the client's KYC to 'High Risk' to make it look compliant."],
  exp:"For unsolicited orders that are unsuitable, the RR must provide a clear warning. If the client still wishes to proceed, the RR must document that the warning was given and that the client directed the trade anyway." },

{ id:8,  el:1, pos:1, q:"Which of the following is considered a 'Financial Goal' rather than an 'Investment Objective'?",
  raw:["Purchasing a $500,000 vacation home in 5 years.",
       "Capital Preservation.",
       "Aggressive Growth.",
       "Speculation."],
  exp:"Objectives describe how the money should behave (Capital Preservation, Aggressive Growth, Speculation). A Goal is a specific, measurable end-result the client wants to achieve." },

{ id:9,  el:1, pos:2, q:"When assessing 'Capacity for Loss,' what is the RR primarily looking at?",
  raw:["The client's objective financial ability to sustain a decline in portfolio value without impacting their standard of living.",
       "How much the client says they are 'willing' to lose before they feel sad.",
       "The client's past experience with buying stocks during the 2008 financial crisis.",
       "The total amount of insurance coverage the client has on their home."],
  exp:"Capacity for Loss is a 'hard' math calculation based on net worth, time horizon, and income. It is distinct from 'Risk Tolerance,' which is the emotional 'soft' side." },

{ id:10, el:1, pos:0, q:"A client opens a Joint Account with their spouse. Who must the RR collect KYC information for?",
  raw:["Both individuals, as each person's risk profile and financial situation must be considered for the account's suitability.",
       "Only the 'primary' earner who provides the most capital.",
       "Only the spouse who will be making the actual trading calls.",
       "Neither; KYC is only required for individual accounts."],
  exp:"For joint accounts, the RR must understand the financial situation and risk profile of all parties involved to ensure the advice is suitable for the 'unit.'" },

{ id:11, el:1, pos:3, q:"An RR is recommending a 'Low Risk' mutual fund to a client. The RR's firm is also the manager of this fund and earns a management fee. What is this an example of?",
  raw:["A Conflict of Interest that must be disclosed to the client in writing.",
       "An illegal trading practice known as 'front-running.'",
       "A 'Primary Distribution' that requires a Takeover Bid Circular.",
       "A breach of the KYC obligation."],
  exp:"Using proprietary products (products owned by the firm) is a conflict of interest. It is allowed, but it must be managed and disclosed to the client so they understand the RR might have a bias." },

{ id:12, el:1, pos:1, q:"Which of the following best defines 'Risk Tolerance'?",
  raw:["The client's emotional willingness to endure market volatility and potential losses in exchange for higher returns.",
       "The client's total net worth minus their total liabilities.",
       "The length of time before the client needs to withdraw 50% of the funds.",
       "The interest rate at which the client borrows money for their mortgage."],
  exp:"Risk tolerance is the 'stomach' for risk—the psychological ability to stay invested when the 'ship starts rocking.'" },

{ id:13, el:1, pos:0, q:"A client wants to open an 'Execution-Only' (Order Execution Only - OEO) account. How does the RR's suitability obligation change?",
  raw:["The RR (or firm) has no obligation to determine the suitability of individual trades in an OEO account.",
       "The RR must still approve every trade for suitability but does not need to give advice.",
       "The RR must provide at least one 'Buy' recommendation per month.",
       "The RR must perform a full KYC update every time the client logs in."],
  exp:"OEO accounts (like discount brokerages) are exempt from the suitability requirement because the firm is not providing any advice; they are simply acting as a 'pipe' for the client's orders." },

{ id:14, el:1, pos:2, q:"When should a 'Suitability Determination' be performed?",
  raw:["Whenever a trade is recommended, a significant amount of cash is deposited, or the KYC is updated.",
       "Only when the account is first opened.",
       "Only when the client complains about a loss.",
       "Once every three years during the mandatory audit."],
  exp:"Suitability is an 'ongoing' duty. Every recommendation and every major change in the account's composition triggers a need to ensure the portfolio still fits the client." },

{ id:15, el:1, pos:3, q:"A client has a 'Low Risk' tolerance and a 'Capital Preservation' objective. Which of the following is a suitable recommendation?",
  raw:["A 1-year Government of Canada Treasury Bill (T-Bill).",
       "A 'hot' new IPO in the biotech sector.",
       "A naked call option on a volatile tech stock.",
       "A 20-year high-yield 'junk' bond."],
  exp:"T-Bills are the 'safest' investment, matching a low risk tolerance and the goal of not losing any principal." },

{ id:16, el:1, pos:1, q:"An RR is assessing a client's 'Investment Knowledge.' The client has traded stocks for 20 years and understands options. How does this impact suitability?",
  raw:["It allows the RR to recommend more complex or high-risk products, as the client is better equipped to understand the risks involved.",
       "It means the RR no longer needs to provide the client with a prospectus.",
       "It allows the RR to skip the KYC update for the next three years.",
       "It means the RR can ignore the 'Client Interest First' standard."],
  exp:"A client with high knowledge can 'give informed consent' to complex strategies that would be unsuitable for a novice investor." },

{ id:17, el:1, pos:2, q:"What is the difference between 'Risk Capacity' and 'Risk Attitude'?",
  raw:["Risk Capacity is the financial ability to take risk; Risk Attitude is the emotional desire to take risk.",
       "Risk Capacity is for young people; Risk Attitude is for retirees.",
       "Risk Capacity is measured in years; Risk Attitude is measured in dollars.",
       "There is no difference; they are synonyms."],
  exp:"You can have a high capacity (you are a billionaire) but a low attitude (you are terrified of losing even $10). Suitability must balance both." },

{ id:18, el:1, pos:0, q:"A client's KYC states their objective is 'Tax Minimization.' What is the RR's role?",
  raw:["To refer the client to a qualified tax professional while ensuring the investment portfolio is structured in a tax-efficient manner (e.g., using TFSAs or RRSPs).",
       "To provide specific legal advice on how to hide assets from the CRA.",
       "To guarantee that the client will pay zero dollars in capital gains tax.",
       "To ignore the tax objective, as only 'Growth' and 'Income' matter for suitability."],
  exp:"RRs are not tax experts. Their job is to identify the need and work with the client's actual tax professionals to build a suitable plan." },

{ id:19, el:1, pos:3, q:"Which of the following must be disclosed to the client at the time of a recommendation under KYP rules?",
  raw:["The risks, the costs (commissions/fees), and the product's features.",
       "The RR's personal net worth.",
       "The names of other clients who have bought the same stock.",
       "The exact price the stock will be in one year."],
  exp:"KYP (Know-Your-Product) requires the RR to understand and explain the 'vitals' of the investment—how it works, what it costs, and how it can fail." },

{ id:20, el:1, pos:1, q:"Which life event would most likely trigger a 'Significant Change' requiring a KYC update?",
  raw:["The client receives a $2,000,000 inheritance.",
       "The client moves to a new apartment in the same city.",
       "The client changes their Netflix subscription.",
       "The client buys a new car with cash they already had."],
  exp:"A massive increase in net worth fundamentally changes the client's 'Capacity for Loss' and likely their goals." },

{ id:21, el:1, pos:2, q:"A 'Relationship Disclosure Document' (RDD) must be provided to a client:",
  raw:["At the time of account opening.",
       "Only if the client asks for it.",
       "Every time a trade is placed.",
       "Only if the RR leaves the firm."],
  exp:"The RDD sets the 'rules of the game'—it explains the fees, the types of advice provided, and the RR's obligations." },

{ id:22, el:1, pos:0, q:"An RR is using 'Analogies' to explain risk to a client. This is a best practice for:",
  raw:["Fulfilling the duty to provide a 'clear explanation' so the client can give informed consent.",
       "Avoiding the need to provide a prospectus.",
       "Making the RR look smarter than the client.",
       "Speeding up the account opening process."],
  exp:"RSE explicitly emphasizes that RRs must be 'translators.' Analogies help retail clients grasp complex risk concepts." },

{ id:23, el:1, pos:3, q:"Which of the following would be an 'Unsuitable' recommendation for a 25-year-old saving for retirement?",
  raw:["Putting 100% of their savings into a 0.5% interest Savings Account.",
       "Investing in a diversified portfolio of global equities.",
       "Using a Tax-Free Savings Account (TFSA) to hold index funds.",
       "Buying a 'Growth' focused mutual fund."],
  exp:"While 'Safe,' a 0.5% return will not meet the long-term goal of retirement due to Inflation Risk. For a 25-year-old, being 'too safe' is actually unsuitable for their long-term objective." },

{ id:24, el:1, pos:1, q:"An RR's 'Product Due Diligence' (KYP) means they must understand:",
  raw:["How the product compares to other similar products available in the market.",
       "The name of the CEO's spouse.",
       "The office hours of the company's customer service line.",
       "The color of the company's logo."],
  exp:"RRs must know why this product is better or more appropriate for the client than the alternatives." },

{ id:25, el:1, pos:0, q:"A client has a 'High Risk' tolerance but a 'Short-Term' (1 year) horizon for a specific goal. How should the RR advise them?",
  raw:["Recommend low-risk, liquid investments because the short time horizon overrides the high risk tolerance.",
       "Recommend high-risk stocks because the client said they like risk.",
       "Tell the client to wait 5 years before investing.",
       "Recommend a 30-year 'Junk' bond."],
  exp:"Time Horizon is a 'hard' constraint. If the money is needed in a year, you cannot risk a 20% market drop, even if the client has a 'strong stomach.'" },

{ id:26, el:1, pos:2, q:"Which KYC component measures the 'Amount' of money a client has versus the 'Debts' they owe?",
  raw:["Net Worth.",
       "Risk Aversion.",
       "Investment Knowledge.",
       "Time Horizon."],
  exp:"Net Worth = Total Assets - Total Liabilities." },

{ id:27, el:1, pos:3, q:"A Registered Representative (RR) is required to be 'registered' with:",
  raw:["The provincial securities commission (e.g., OSC) and be an employee of a CIRO-regulated Dealer Member.",
       "The Bank of Canada.",
       "The local Chamber of Commerce.",
       "The TSX Trading Floor."],
  exp:"Registration is mandatory to ensure proficiency and ethical standards are met before an individual can give investment advice to the public." },

// ══ ELEMENT 2 — Q28-37 ══
{ id:28, el:2, pos:0, q:"What is the fundamental 'Inverse Relationship' in the bond market?",
  raw:["When market interest rates rise, the market price of existing bonds falls.",
       "When market interest rates rise, the market price of existing bonds rises.",
       "When a company's profits rise, its bond prices fall.",
       "When inflation falls, bond coupons increase."],
  exp:"This is the most important rule in fixed income. If new bonds are issued at 5%, your old 3% coupon bond is less attractive, so its price must drop for someone to want to buy it." },

{ id:29, el:2, pos:1, q:"A 'Strip Bond' (Zero-Coupon Bond) is an investment that:",
  raw:["Does not pay periodic interest but is purchased at a deep discount and matures at its full face value.",
       "Pays a very high monthly dividend.",
       "Can be converted into common shares at the client's request.",
       "Is only issued by the US Government."],
  exp:"The 'interest' on a strip bond is the capital gain earned between the discount price paid and the $1,000 par value received at maturity." },

{ id:30, el:2, pos:2, q:"Which bond would have the highest 'Interest Rate Risk' (sensitivity to rate changes)?",
  raw:["A 30-year Government Bond with a 2% coupon.",
       "A 2-year Corporate Bond with a 5% coupon.",
       "A 91-day Treasury Bill.",
       "A 5-year GIC."],
  exp:"Duration is the measure of risk. The longer the term to maturity and the lower the coupon, the more the bond's price will swing when interest rates move. A 30-year bond is much more sensitive than a 2-year bond." },

{ id:31, el:2, pos:3, q:"A client buys a bond halfway between interest payment dates. Which correctly describes 'Accrued Interest'?",
  raw:["The buyer pays the seller the portion of the next coupon earned from the last payment date up to, but not including, the settlement date.",
       "The seller pays the buyer a penalty for selling the bond before the coupon date.",
       "The interest is forfeited by both parties and kept by the issuing corporation.",
       "Accrued interest is only calculated for Government of Canada bonds, not corporate bonds."],
  exp:"In the bond market, interest 'accrues' daily. Since the buyer will receive the full next coupon from the issuer, they must compensate the seller for the days the seller held the bond during that period." },

{ id:32, el:2, pos:1, q:"An RR observes that long-term bond yields are currently lower than short-term bond yields. What is this market phenomenon called?",
  raw:["An Inverted Yield Curve, which is often interpreted as a signal of a looming recession.",
       "A Normal Yield Curve, indicating healthy economic expansion.",
       "A Flat Yield Curve, showing that inflation is perfectly stable.",
       "A Positive Sloping Curve, suggesting that interest rates will rise indefinitely."],
  exp:"A normal yield curve slopes upward. An 'inverted' curve (short rates > long rates) suggests investors expect rates to fall in the future due to economic slowing." },

{ id:33, el:2, pos:2, q:"A 'Callable' bond is generally considered to have which specific risk for the investor?",
  raw:["Reinvestment Risk, because the issuer will likely 'call' the bond back when interest rates fall, forcing the investor to reinvest at lower rates.",
       "Inflation Risk, because the bond's face value will decrease if the CPI rises.",
       "Liquidity Risk, because callable bonds cannot be sold on the secondary market.",
       "Default Risk, because only companies near bankruptcy issue callable bonds."],
  exp:"Issuers call bonds when they can refinance at a cheaper rate. This happens when market rates drop, leaving the investor with cash that can now only be reinvested in lower-yielding securities." },

{ id:34, el:2, pos:0, q:"Which of the following best describes a 'Convertible Bond'?",
  raw:["A corporate bond that grants the holder the right to exchange the bond for a fixed number of common shares of the same corporation.",
       "A government bond that can be switched from Canadian dollars to US dollars at any time.",
       "A bond that automatically turns into a GIC if the stock market crashes.",
       "A bond that has no maturity date and pays interest forever."],
  exp:"Convertibles are 'hybrid' securities. They offer the safety of a bond (fixed income) with the potential 'upside' of equity if the company's stock price rises significantly." },

{ id:35, el:2, pos:3, q:"If a bond is trading at a 'Premium' (Price > 100), which of the following is true?",
  raw:["The Yield to Maturity (YTM) is lower than the Coupon Rate.",
       "The Yield to Maturity (YTM) is higher than the Coupon Rate.",
       "The bond is about to default.",
       "The current market interest rates are higher than the bond's coupon."],
  exp:"If a bond's coupon is 5% but market rates are 3%, everyone wants that bond. They bid the price up above par. Because you paid more than the $1,000 you get back at maturity, your total yield (YTM) is dragged down below the 5% coupon." },

{ id:36, el:2, pos:1, q:"Which organization provides credit ratings specifically for Canadian debt issuers to help RRs assess 'Credit Risk'?",
  raw:["S&P Global Ratings, Moody's, or DBRS Morningstar.",
       "The Canada Revenue Agency (CRA).",
       "The Bank of Canada.",
       "CIRO's internal marketing department."],
  exp:"RRs use these agencies to determine the 'quality' of a bond. AAA is the highest (safest), while anything below BBB (for S&P/DBRS) is considered 'High Yield' or 'Junk.'" },

{ id:37, el:2, pos:2, q:"A 'Real Return Bond' (RRB) is specifically designed to protect an investor against:",
  raw:["Inflation Risk, as the principal amount is adjusted semi-annually based on the Consumer Price Index (CPI).",
       "Interest Rate Risk, as the price never changes regardless of Bank of Canada moves.",
       "Currency Risk, as it is always paid in gold bullion.",
       "Business Risk, as the government guarantees the company will never go bankrupt."],
  exp:"RRBs ensure that the investor's purchasing power stays constant. If inflation is 5%, the principal of the bond grows by 5%, ensuring the interest is calculated on a larger, inflation-adjusted base." },

// ══ ELEMENT 3 — Q38-49 ══
{ id:38, el:3, pos:0, q:"Which of the following is a primary right of a 'Common Shareholder'?",
  raw:["The right to vote on the election of the Board of Directors and major corporate changes.",
       "The right to receive a fixed dividend before any interest is paid to bondholders.",
       "The right to be the first person paid if the company goes bankrupt and is liquidated.",
       "The right to manage the day-to-day operations of the company's retail stores."],
  exp:"Common shareholders are the owners. They have the right to vote (usually one vote per share) but sit at the bottom of the 'Capital Structure' for payments." },

{ id:39, el:3, pos:3, q:"A 'Cumulative' Preferred Share ensures that:",
  raw:["If the company misses a dividend payment, the unpaid amount 'accumulates' and must be paid in full before common shareholders receive any dividends.",
       "The investor receives a bonus share for every year they hold the stock.",
       "The dividend increases automatically every time the company's stock price rises.",
       "The shareholder can vote twice in every election."],
  exp:"This 'cumulative' feature is a safety net for income-seeking investors. It protects them from being skipped while owners (common shareholders) still get paid." },

{ id:40, el:3, pos:1, q:"A corporation announces a '2-for-1 Stock Split.' If a client owns 100 shares at $60 per share, what is the result after the split?",
  raw:["They own 200 shares at $30 per share; the total value of their investment remains $6,000.",
       "They own 200 shares at $60 per share; the total value of their investment doubles.",
       "They own 50 shares at $120 per share.",
       "The split has no effect on the number of shares, only the voting rights."],
  exp:"Stock splits are 'cosmetic.' They increase the number of shares and lower the price to make the stock more accessible to retail investors, but the client's percentage of ownership stays exactly the same." },

{ id:41, el:3, pos:2, q:"What is the main difference between a 'Right' and a 'Warrant'?",
  raw:["Rights are short-term and issued to existing shareholders to prevent dilution; Warrants are long-term and often used as 'sweeteners' for bond or preferred issues.",
       "Rights are only for bonds, while Warrants are only for stocks.",
       "Warrants are mandatory to exercise, while Rights are optional.",
       "Rights are issued by the government, while Warrants are issued by the TSX."],
  exp:"Rights usually expire in weeks, while Warrants can last years. Both give the holder the right to buy shares at a set price (the exercise price)." },

{ id:42, el:3, pos:0, q:"Which of the following best describes 'Preferred Shares'?",
  raw:["A hybrid security that pays a fixed dividend and has a higher claim on assets than common shares, but usually carries no voting rights.",
       "A type of debt that must be repaid within 90 days.",
       "Shares that are only available to the company's CEO and executives.",
       "Shares that guarantee the price will never fall below the purchase price."],
  exp:"Preferreds are like a mix of a bond (fixed income) and a stock (ownership). They are sensitive to interest rates, much like bonds." },

{ id:43, el:3, pos:3, q:"A client is interested in 'Participating' Preferred Shares. What is the unique feature of these shares?",
  raw:["They receive their fixed dividend, plus an additional share of the profits if the common share dividends exceed a certain amount.",
       "They allow the holder to participate in the company's daily board meetings.",
       "They can be sold back to the company at any time for a 20% profit.",
       "They automatically convert into Bitcoin if the US dollar weakens."],
  exp:"Participating preferreds allow the holder to 'participate' in the company's success beyond just the fixed dividend rate." },

{ id:44, el:3, pos:1, q:"What is the purpose of a 'Proxy' in the context of a shareholder meeting?",
  raw:["It is a written authorization that allows a shareholder to delegate their voting rights to another person or management.",
       "It is a document that guarantees a dividend payment.",
       "It is a type of insurance policy against stock market losses.",
       "It is a secret code used to buy shares before an IPO."],
  exp:"Most retail shareholders cannot attend meetings in person. Using a proxy allows their voice (votes) to be heard on important matters like board elections." },

{ id:45, el:3, pos:2, q:"For a Canadian tax-paying investor, which is generally taxed more favorably in a non-registered account?",
  raw:["Dividends from Canadian corporations (due to the Dividend Tax Credit).",
       "Interest income from corporate bonds.",
       "Interest income from GICs.",
       "Foreign interest income from US Treasuries."],
  exp:"The Canadian government provides a tax credit on dividends to avoid 'double taxation.' This makes dividends more tax-efficient than interest income, which is taxed at the investor's full marginal rate." },

{ id:46, el:3, pos:0, q:"Which type of share would likely be the most sensitive to a rise in market interest rates?",
  raw:["Fixed-Rate Preferred Shares.",
       "Common Shares of a high-growth tech company.",
       "Floating-Rate Preferred Shares.",
       "Penny stocks on the Venture exchange."],
  exp:"Fixed-rate preferreds behave like bonds. If interest rates go up, the fixed dividend becomes less attractive, causing the market price of the share to fall. Floating-rate shares are less sensitive because their dividends adjust upward with the market." },

{ id:47, el:3, pos:3, q:"'Restricted Shares' are shares that:",
  raw:["Carry a right to vote that is less than the voting right per share of another class of the same issuer's shares.",
       "Cannot be sold for at least 50 years.",
       "Are only available to investors living in specific provinces.",
       "Can only be held in a TFSA."],
  exp:"Some companies issue 'Class A' and 'Class B' shares where one class has 10 votes per share and the other has only 1. The class with fewer votes is considered 'restricted.'" },

{ id:48, el:3, pos:1, q:"What is a 'Stock Dividend'?",
  raw:["A dividend paid in the form of additional shares rather than cash.",
       "A dividend that is only paid if the stock price goes up by 10%.",
       "A dividend that must be used to buy more bonds.",
       "A cash payment that is sent to the client via a physical cheque only."],
  exp:"Companies often use stock dividends to reward shareholders without depleting the company's cash reserves." },

{ id:49, el:3, pos:2, q:"Equities are generally considered a better hedge against which macro factor compared to long-term bonds?",
  raw:["Inflation, because companies can often raise their prices to maintain profit margins as the cost of living rises.",
       "Interest rate increases, because stock prices always rise when rates rise.",
       "Default risk, because companies never go bankrupt.",
       "Currency devaluation, because stocks are not impacted by the value of the dollar."],
  exp:"Bonds pay a fixed amount that loses value as inflation rises. Stocks represent ownership in real businesses that can adjust their prices, potentially allowing earnings (and stock prices) to keep pace with inflation." },

// ══ ELEMENT 4 — Q50-63 ══
{ id:50, el:4, pos:0, q:"On a Balance Sheet, 'Working Capital' is calculated as:",
  raw:["Current Assets minus Current Liabilities.",
       "Total Assets minus Total Liabilities.",
       "Cash minus Accounts Payable.",
       "Net Income minus Dividends."],
  exp:"Working Capital measures a company's ability to meet its short-term obligations using its short-term assets. A negative number is a major red flag for liquidity." },

{ id:51, el:4, pos:3, q:"An RR is looking at an Income Statement and sees 'EBITDA.' What does this acronym stand for?",
  raw:["Earnings Before Interest, Taxes, Depreciation, and Amortization.",
       "Every Business Income Total Daily Amount.",
       "Earnings Basic Income Total Dividend Average.",
       "Equity Balance Income Total Debt Amount."],
  exp:"EBITDA is used to see the 'cash-generating power' of the business operations by stripping away non-cash accounting items (depreciation) and financing/tax choices." },

{ id:52, el:4, pos:1, q:"In a Statement of Cash Flows, which section would include the cash received from selling an old office building?",
  raw:["Cash Flow from Investing Activities.",
       "Cash Flow from Operating Activities.",
       "Cash Flow from Financing Activities.",
       "Cash Flow from Extraordinary Activities."],
  exp:"Investing activities track the purchase and sale of 'long-term assets' (Property, Plant, and Equipment)." },

{ id:53, el:4, pos:2, q:"Which ratio would an RR use to determine how much of a company is funded by 'Lenders' versus 'Owners'?",
  raw:["Debt-to-Equity Ratio.",
       "Gross Profit Margin.",
       "Current Ratio.",
       "Inventory Turnover."],
  exp:"A high Debt-to-Equity ratio (e.g., 2.5) means the company is heavily leveraged, which increases the risk of bankruptcy if profits fall." },

{ id:54, el:4, pos:0, q:"A stock has a P/E Ratio of 40x, while its industry average is 15x. What is the most likely interpretation?",
  raw:["Investors expect the company to have significantly higher future earnings growth compared to its peers.",
       "The stock is a 'deep value' bargain.",
       "The company is about to go bankrupt.",
       "The company has no debt."],
  exp:"A high P/E means the market is paying a 'premium' today because it expects much bigger profits tomorrow." },

{ id:55, el:4, pos:3, q:"Which of the following is a 'Liquidity Ratio'?",
  raw:["Quick Ratio (Acid Test).",
       "Return on Equity (ROE).",
       "Interest Coverage Ratio.",
       "Asset Turnover."],
  exp:"Liquidity ratios measure the 'right now' survival of the company—can they pay the bills due this month?" },

{ id:56, el:4, pos:1, q:"'Net Profit Margin' is calculated as:",
  raw:["Net Income divided by Revenue.",
       "Gross Profit divided by Revenue.",
       "Revenue minus COGS.",
       "Net Income divided by Shareholders' Equity."],
  exp:"This tells you how many cents of 'actual profit' the company keeps for every $1.00 of sales." },

{ id:57, el:4, pos:0, q:"If a company has an 'Inventory Turnover' of 12, what does this mean in simple terms?",
  raw:["The company sells and replaces its entire inventory approximately once every month.",
       "The company has 12 different types of products.",
       "The company's inventory is 12% of its total assets.",
       "It takes 12 days to manufacture one product."],
  exp:"Turnover = 12 times a year. 12 months / 12 = 1. High turnover is usually a sign of efficiency." },

{ id:58, el:4, pos:2, q:"What is the 'Dividend Payout Ratio'?",
  raw:["The percentage of Net Income paid out to shareholders as dividends.",
       "The dollar amount of the dividend divided by the stock price.",
       "The amount of dividends paid divided by the company's total debt.",
       "The number of shares an investor receives instead of cash."],
  exp:"A payout ratio of 30% means the company keeps 70% of its profits to grow. A ratio of 110% means they are paying out more than they earned, which is a major red flag." },

{ id:59, el:4, pos:3, q:"'Return on Equity' (ROE) measures:",
  raw:["How much profit the company generates with the money shareholders have invested.",
       "The percentage change in the stock price over one year.",
       "The amount of dividends paid per common share.",
       "The total assets divided by the number of shares."],
  exp:"ROE is the 'ultimate' measure of management efficiency—how good are they at making money using your (the owner's) capital?" },

{ id:60, el:4, pos:1, q:"'Book Value per Share' is best described as:",
  raw:["The theoretical 'liquidation' value of a single share based on accounting records (Assets - Liabilities).",
       "The current price of the stock on the TSX.",
       "The highest price the stock has ever reached.",
       "The value of the company's physical books and libraries."],
  exp:"If a stock's market price is $5.00 but its Book Value is $10.00, it might be an 'undervalued' bargain—or it might mean the assets on the books are actually worthless." },

{ id:61, el:4, pos:2, q:"An RR is explaining the concept of 'Liquidity' to a retail client by looking at market data. Which is the most direct indicator that a stock is 'illiquid'?",
  raw:["A wide Bid-Ask spread relative to the share price.",
       "A high daily trading volume.",
       "A large market capitalization.",
       "A high dividend payout ratio."],
  exp:"Liquidity refers to how easily an asset can be converted to cash without a significant price impact. A wide spread means the investor loses value immediately upon purchase, which is characteristic of illiquid markets." },

{ id:62, el:4, pos:0, q:"A public company fails to file its audited annual financial statements by the regulatory deadline. What regulatory action will likely be taken?",
  raw:["A Cease Trade Order (CTO) will be issued, freezing all trading in the security.",
       "The company will be forced to pay a special dividend.",
       "The RR is legally required to sell all client holdings 'at market.'",
       "The TSX will automatically lower the stock's price by 20%."],
  exp:"Continuous disclosure is a cornerstone of market integrity. If a company fails to provide financial information, regulators issue a CTO to prevent investors from trading on incomplete or stale information." },

{ id:63, el:4, pos:3, q:"When comparing portfolio performance to the S&P/TSX Composite Index, why is it essential to use the 'Total Return' version?",
  raw:["Because it accounts for both capital gains and the reinvestment of dividends, providing an 'apples-to-apples' comparison.",
       "Because 'Price Return' indices are only for professional institutional investors.",
       "Because Total Return indices are always higher and make the RR look better.",
       "Because dividends are not considered part of a client's actual profit."],
  exp:"For most investors, dividends are a major component of return. Comparing a portfolio that receives dividends to a 'Price Return' index (which ignores them) would falsely suggest the portfolio is outperforming the market." },

// ══ ELEMENT 5 — Q64-79 ══
{ id:64, el:5, pos:1, q:"In a typical mutual fund structure, which party is responsible for the 'safekeeping' of the fund's assets and ensuring they are kept separate from the fund manager's own assets?",
  raw:["The Custodian.",
       "The Trustee.",
       "The Portfolio Manager.",
       "The Registrar."],
  exp:"The Custodian (usually a large trust company or bank) acts as the vault. This separation of duties is a critical regulatory protection to prevent the fund manager from misappropriating investor capital." },

{ id:65, el:5, pos:2, q:"What is a primary difference between an Open-End Mutual Fund and an Exchange-Traded Fund (ETF)?",
  raw:["Mutual funds are priced once daily at the end-of-day NAVPS, while ETFs trade throughout the day on a stock exchange.",
       "Mutual funds are only for bonds, while ETFs are only for stocks.",
       "ETFs guarantee a profit, while mutual funds do not.",
       "Mutual funds have no Management Expense Ratio (MER)."],
  exp:"Mutual funds use 'forward pricing,' where you get the price calculated after the market closes. ETFs provide 'real-time' liquidity, allowing investors to buy or sell at market prices during the trading day." },

{ id:66, el:5, pos:0, q:"A mutual fund has $100 million in total assets, $5 million in liabilities, and 5 million units outstanding. What is the NAVPS?",
  raw:["$19.00","$20.00","$21.00","$1.00"],
  exp:"NAVPS = (Total Assets - Total Liabilities) / Units Outstanding. ($100M - $5M) / 5M = $19.00." },

{ id:67, el:5, pos:3, q:"An investor is concerned about 'Tracking Error' in their portfolio. This term is most commonly associated with which of the following?",
  raw:["Passive ETFs that fail to perfectly replicate the performance of their underlying benchmark index.",
       "Mutual fund managers who trade too frequently.",
       "The risk that a bond issuer will default on its interest payments.",
       "The difference between a stock's Bid price and its Ask price."],
  exp:"Passive products aim to match an index. Tracking error occurs due to management fees, transaction costs, or the inability of the manager to hold every single security in the index in the exact required proportion." },

{ id:68, el:5, pos:1, q:"Which feature is unique to Segregated Funds compared to standard Mutual Funds?",
  raw:["They offer death and maturity guarantees (typically 75% to 100% of the principal) provided by an insurance company.",
       "They allow for the tax-free withdrawal of all capital gains.",
       "They are only available to accredited investors with high net worth.",
       "They have significantly lower management fees than ETFs."],
  exp:"Segregated funds are insurance contracts. These guarantees make them popular for conservative investors, though the cost of the guarantee usually leads to a higher MER than a standard mutual fund." },

{ id:69, el:5, pos:2, q:"A client is considering a 'Principal Protected Note' (PPN). What is the most critical risk regarding the 'Principal Protection' feature?",
  raw:["The protection is subject to the credit risk of the issuer; if the bank that issued the note fails, the protection is lost.",
       "The protection only applies if the stock market goes up.",
       "The investor must pay a 20% penalty to get their principal back at maturity.",
       "The protection is only valid for the first 30 days of the investment."],
  exp:"PPNs are debt obligations of the issuing bank. Unlike GICs (which may be covered by CDIC), the guarantee on a PPN depends entirely on the bank's ability to stay solvent." },

{ id:70, el:5, pos:0, q:"'Hedge Funds' often use a '2 and 20' fee structure. What does the '20' typically represent?",
  raw:["A performance fee equal to 20% of the profits generated by the fund (often above a hurdle rate).",
       "A flat 20% sales commission paid to the RR.",
       "A 20% penalty for withdrawing money within the first year.",
       "A 20% guarantee on the principal."],
  exp:"Hedge funds are 'active' and seek 'absolute returns.' The performance fee incentivizes the manager to generate profit, but it can also lead to higher risk-taking." },

{ id:71, el:5, pos:3, q:"In the ETF 'Creation and Redemption' process, which participant is responsible for assembling the 'basket' of securities to exchange for new ETF units?",
  raw:["The Designated Broker (Authorized Participant).",
       "The individual retail investor.",
       "The TSX Trading Floor.",
       "The Bank of Canada."],
  exp:"This institutional process ensures that the ETF's market price stays close to its underlying Net Asset Value (NAV) through arbitrage." },

{ id:72, el:5, pos:1, q:"Why do 'Passive' index funds generally have lower Management Expense Ratios (MERs) than 'Active' funds?",
  raw:["Because they follow a mechanical rules-based strategy that does not require an expensive team of research analysts and star managers.",
       "Because they are not required to pay any taxes on their capital gains.",
       "Because they are not allowed to charge any commissions to the client.",
       "Because they only invest in Government of Canada bonds."],
  exp:"Active management is expensive. Passive management 'buys the market,' significantly reducing the overhead costs of the fund." },

{ id:73, el:5, pos:2, q:"What is the primary purpose of the 'Fund Facts' document provided to mutual fund investors?",
  raw:["To provide a plain-language summary of the fund's key features, risks, and costs in a standardized, easy-to-read format.",
       "To list every single trade the manager made in the last 24 hours.",
       "To provide a legal guarantee that the fund will outperform the TSX.",
       "To provide the RR's personal cell phone number."],
  exp:"Under Canadian law, a 'Fund Facts' must be delivered to a client before they buy a mutual fund to ensure they understand what they are buying and what it costs." },

{ id:74, el:5, pos:0, q:"An ETF is trading at $50.05 on the exchange, but its actual Net Asset Value (NAV) is $49.95. This situation is called trading at a:",
  raw:["Premium.","Discount.","Par value.","Deficit."],
  exp:"Price > NAV = Premium. Price < NAV = Discount." },

{ id:75, el:5, pos:3, q:"'Real Estate Investment Trusts' (REITs) allow retail investors to:",
  raw:["Gain exposure to a diversified pool of professionally managed commercial or residential properties with high liquidity.",
       "Avoid paying all personal income taxes on their investment returns.",
       "Purchase their own primary residence with a 0% interest rate.",
       "Manage the daily maintenance of the buildings held by the trust."],
  exp:"REITs turn 'bricks and mortar' into liquid, tradable units. They are popular for 'Income' clients because they must distribute a large portion of their cash flow to unitholders." },

{ id:76, el:5, pos:1, q:"Which of the following is a characteristic of a 'Closed-End Fund'?",
  raw:["They issue a fixed number of shares at an IPO, which then trade on an exchange like a stock, often at a premium or discount to NAV.",
       "They must issue new shares every time an investor wants to buy in.",
       "They allow for daily redemptions directly with the fund manager.",
       "They have no management fees."],
  exp:"Unlike 'Open-end' mutual funds (which grow and shrink daily), closed-end funds have a static capital structure. To exit, you must find another buyer on the stock exchange." },

{ id:77, el:5, pos:2, q:"An RR is recommending a 'Money Market Fund.' What is the typical expectation for the NAVPS of such a fund?",
  raw:["The fund aims to maintain a stable NAVPS (typically $10.00 or $1.00) while providing a low-risk interest return.",
       "The fund aims for 20% annual capital growth.",
       "The fund's price will fluctuate wildly based on the price of gold.",
       "The fund has no NAVPS because it only holds cash."],
  exp:"Money market funds are 'cash equivalents.' Their goal is safety and liquidity, not capital gains." },

{ id:78, el:5, pos:0, q:"A 'Wrap Account' is best described as a managed product where:",
  raw:["Several different investment services (management, trading, reporting) are bundled together for a single, all-inclusive fee based on Assets Under Management (AUM).",
       "The client is required to pay a separate commission for every single trade.",
       "The RR is prohibited from speaking to the client more than once a year.",
       "The portfolio is 'wrapped' in a life insurance policy to avoid all risks."],
  exp:"Wrap accounts align the interests of the RR and the client because the RR's compensation is based on the account's value, not the volume of trading." },

{ id:79, el:5, pos:3, q:"The 'Reset' feature in a Segregated Fund allows the contract holder to:",
  raw:["Lock in a higher market value of the fund as the new guaranteed minimum for death or maturity benefits.",
       "Change the fund manager whenever they choose.",
       "Avoid paying the MER for the first three years.",
       "Transfer the fund into a standard bank account without any tax consequences."],
  exp:"If the market value of the fund grows significantly, a 'reset' allows the investor to 'protect' those gains by making the current higher value the new floor for the insurance guarantee." },

// ══ ELEMENT 6 — Q80-94 ══
{ id:80, el:6, pos:1, q:"According to Modern Portfolio Theory (MPT), which type of risk can be eliminated by adding more, non-correlated stocks to a portfolio?",
  raw:["Unsystematic Risk (Specific Risk).",
       "Systematic Risk (Market Risk).",
       "Interest Rate Risk.",
       "Inflation Risk."],
  exp:"Unsystematic risk is the risk of a single company (e.g., a CEO scandal or a factory fire). By holding 30+ stocks in different industries, these specific events cancel each other out. Systematic risk (e.g., a global recession) cannot be diversified away." },

{ id:81, el:6, pos:2, q:"On the 'Efficient Frontier,' a portfolio is considered 'optimal' if it:",
  raw:["Provides the maximum possible expected return for a given level of risk.",
       "Has a 0% standard deviation.",
       "Consists only of Government of Canada bonds.",
       "Has a Beta of exactly 0."],
  exp:"MPT states that for every level of 'pain' (risk), there is a 'maximum gain' portfolio. Anything below the frontier is inefficient because you could get more return for the same risk." },

{ id:82, el:6, pos:0, q:"If two stocks have a Correlation of -1.0, how will they move in relation to each other?",
  raw:["They will move in perfectly opposite directions; when one goes up, the other goes down by the same relative amount.",
       "They will move together perfectly in the same direction.",
       "There is no relationship between their movements.",
       "They will both always be flat."],
  exp:"A -1.0 correlation provides the maximum diversification benefit. A +1.0 correlation provides no diversification benefit." },

{ id:83, el:6, pos:3, q:"A stock has a 'Beta' of 1.5. If the overall market (S&P/TSX) rises by 10%, what is the expected movement of this stock?",
  raw:["It is expected to rise by 15%.",
       "It is expected to rise by 10%.",
       "It is expected to fall by 5%.",
       "It is expected to stay the same."],
  exp:"Beta measures sensitivity to the market. Beta > 1 is 'Aggressive' (more volatile than the market). Beta < 1 is 'Defensive' (less volatile than the market). 1.5 × 10% = 15%." },

{ id:84, el:6, pos:1, q:"'Tactical Asset Allocation' (TAA) is a portfolio strategy where the manager:",
  raw:["Makes short-term adjustments to the asset mix to take advantage of perceived market opportunities or 'tilts.'",
       "Sets a long-term target (e.g., 60/40) and never changes it regardless of market conditions.",
       "Only buys gold and silver.",
       "Only invests in the stocks listed in the Dow Jones Industrial Average."],
  exp:"TAA is 'active.' It assumes the manager can 'time' the market or identify sectors that will temporarily outperform." },

{ id:85, el:6, pos:2, q:"'Strategic Asset Allocation' (SAA) is primarily based on:",
  raw:["The client's long-term KYC factors, such as risk tolerance and time horizon, to create a stable 'base' policy mix.",
       "The RR's 'gut feeling' about the market this morning.",
       "The most popular social media trends.",
       "A computer model that trades every 5 minutes."],
  exp:"SAA is the 'anchor' of the portfolio. It is designed to meet the client's long-term goals through a diversified mix that is only changed if the client's life situation changes." },

{ id:86, el:6, pos:0, q:"A portfolio has a target mix of 60% stocks and 40% bonds. After a year of strong stock market growth, the mix is now 70% stocks and 30% bonds. What is the process of selling stocks and buying bonds to return to the original target?",
  raw:["Rebalancing.",
       "Benchmarking.",
       "Devaluation.",
       "Aggregation."],
  exp:"Rebalancing is a 'buy low, sell high' discipline. It ensures the portfolio does not become 'too risky' for the client's KYC as the market fluctuates." },

{ id:87, el:6, pos:3, q:"In the context of portfolio risk, 'Standard Deviation' is a statistical measure of:",
  raw:["Volatility; the degree to which a portfolio's returns fluctuate around its average return.",
       "The total amount of dividends paid by the companies in the portfolio.",
       "The number of different countries represented in the portfolio.",
       "The RR's commission rate."],
  exp:"A higher standard deviation means a 'bumpier' ride for the client. This is the standard tool used to quantify 'risk' in finance." },

{ id:88, el:6, pos:1, q:"Which of the following is an advantage of 'Active' management over 'Passive' management?",
  raw:["The potential for the manager to 'outperform' the market (generate Alpha) by selecting superior securities.",
       "Significantly lower management fees (MERs).",
       "Guaranteed performance that matches the index exactly.",
       "Lower tax implications due to fewer trades."],
  exp:"Active managers claim they can find 'undervalued' stocks or avoid 'bad' companies that a passive index is forced to buy." },

{ id:89, el:6, pos:2, q:"To achieve the benefits of 'Optimal Diversification' and eliminate most unsystematic risk, a portfolio should typically hold at least:",
  raw:["30 to 40 non-correlated securities across different sectors.",
       "2 or 3 of the client's favorite stocks.",
       "500 stocks all from the energy sector.",
       "Only 1 stock, provided it is a 'blue-chip' bank."],
  exp:"Studies show that after about 30-40 stocks, adding more does not significantly reduce risk any further, though it may increase costs." },

{ id:90, el:6, pos:0, q:"The 'Sharpe Ratio' is used to assess:",
  raw:["Risk-adjusted performance; it calculates how much 'excess return' a portfolio generated for every unit of risk taken.",
       "The total amount of debt a client has relative to their house value.",
       "The speed at which an RR places a trade.",
       "The physical size of the company's headquarters."],
  exp:"A higher Sharpe Ratio is better. It helps RRs determine if a fund manager is actually 'skilled' or if they just got lucky by taking on excessive, dangerous risk." },

{ id:91, el:6, pos:3, q:"An RR is explaining why a client's 'Balanced' portfolio returned 9% while the benchmark returned 7%. What is the term for the analysis that identifies whether this outperformance came from 'Asset Allocation' or 'Security Selection'?",
  raw:["Performance Attribution.",
       "Standard Deviation.",
       "Tactical Rebalancing.",
       "Modern Portfolio Theory."],
  exp:"Performance attribution breaks down the 'excess return' (Alpha). It helps the RR explain to the client exactly where value was added—by having more stocks than the benchmark (allocation) or by picking specific stocks that beat the market (selection)." },

{ id:92, el:6, pos:1, q:"Which of the following is a primary benefit of 'Rebalancing' a portfolio back to its Strategic Asset Allocation (SAA)?",
  raw:["It enforces a 'buy low, sell high' discipline by selling assets that have performed well and buying those that have underperformed.",
       "It guarantees that the portfolio will never experience a negative return during a recession.",
       "It eliminates the need for any management fees or MERs.",
       "It ensures the portfolio has a Beta of exactly 0 at all times."],
  exp:"Without rebalancing, a portfolio drifts. If stocks go up, they become a larger part of the portfolio, making the portfolio riskier than the client's KYC allows. Rebalancing resets the risk levels." },

{ id:93, el:6, pos:2, q:"A client is concerned that their portfolio is 'too concentrated' in Canadian banks. Which type of diversification would most effectively reduce this specific 'Sector Risk'?",
  raw:["Industrial Diversification (adding stocks from different sectors like Technology, Healthcare, and Utilities).",
       "Geographic Diversification (buying more US and International stocks).",
       "Asset Class Diversification (buying more bonds).",
       "Currency Diversification (holding only Euro-denominated assets)."],
  exp:"Sector or Industrial diversification ensures that a downturn in one specific industry (like Banking) doesn't devastate the entire portfolio." },

{ id:94, el:6, pos:0, q:"In the context of 'Portfolio Monitoring,' why is it important to consider transaction costs and taxes during the rebalancing process?",
  raw:["Because excessive trading can generate costs and realized capital gains that may outweigh the benefits of returning to the target asset mix.",
       "Because CIRO rules prohibit rebalancing more than once every five years.",
       "Because taxes are only paid when a portfolio is 'out of balance.'",
       "Because transaction costs are paid to the Bank of Canada, not the Dealer Member."],
  exp:"A professional RR must balance the 'mathematical' need to rebalance with the 'real-world' costs to the client. Sometimes, 'doing nothing' is the most suitable choice if the portfolio has only drifted slightly." },

// ══ ELEMENT 7 — Q95-108 ══
{ id:95, el:7, pos:3, q:"According to the Client Focused Reforms (CFRs), what is the 'priority' standard when making a suitability determination?",
  raw:["The RR must put the client's interest first when making a suitability determination.",
       "The RR must put the firm's profit first to ensure the Dealer Member stays solvent.",
       "The RR must always pick the product with the highest historical 1-year return.",
       "The RR must ensure the client pays the highest possible commission to support the industry."],
  exp:"This is the core 'Best Interest' standard. Suitability must be determined objectively based on the client's KYC, not on what earns the RR the most money." },

{ id:96, el:7, pos:1, q:"An RR recommends a switch from a 'Front-End Load' mutual fund to a 'No-Load' fund with a lower MER. What must the RR disclose regarding the 'Cost' of this recommendation?",
  raw:["All fees, charges, and potential tax implications associated with selling the old fund and buying the new one.",
       "Only the RR's personal commission.",
       "Nothing, as 'No-Load' funds have zero costs for the client.",
       "Only the name of the new fund manager."],
  exp:"Full cost transparency is mandatory. Even if the new fund is 'better,' the act of switching might trigger a capital gain or a 'Short-term Trading Fee' that the client must understand beforehand." },

{ id:97, el:7, pos:2, q:"A client has a 'Low Risk' tolerance but wants to invest in a 'High Yield' corporate bond fund to get a 7% return. How should the RR respond?",
  raw:["The RR should explain the mismatch between the 'High Risk' of the junk bonds and the client's 'Low Risk' tolerance, and advise against the recommendation.",
       "The RR should place the trade immediately to keep the client happy.",
       "The RR should change the client's risk tolerance to 'High' without telling them.",
       "The RR should guarantee the 7% return using the firm's insurance policy."],
  exp:"Suitability is a 'veto' power. If a product doesn't fit the KYC, the RR has a professional duty to warn the client and potentially refuse to make the recommendation." },

{ id:98, el:7, pos:0, q:"When recommending a 'Structured Product' (like a PPN) to a retail client, the RR's KYP duty requires them to understand:",
  raw:["The specific 'payoff formula,' the credit risk of the issuer, and any liquidity restrictions (lock-up periods).",
       "Only the name of the bank that issued it.",
       "The names of other clients who have already bought the same product.",
       "The exact date the product will reach its highest value."],
  exp:"Structured products are complex. An RR cannot recommend what they do not understand, especially regarding how the client actually gets their money back." },

{ id:99, el:7, pos:3, q:"A client is in a high tax bracket and has a 'Non-Registered' account. Which recommendation is generally more 'tax-efficient'?",
  raw:["A Canadian Equity ETF that pays eligible dividends and generates capital gains.",
       "A High-Interest Savings Account (HISA) paying 5% interest.",
       "A Corporate Bond fund paying 6% interest.",
       "A US-based Real Estate Investment Trust (REIT) paying foreign income."],
  exp:"In Canada, interest income is taxed at the highest rate (100% of the marginal rate), while capital gains (50% inclusion) and Canadian dividends (tax credit) are taxed much more favorably." },

{ id:100, el:7, pos:1, q:"Why must an RR compare a recommended product to 'Reasonable Alternatives' available at the firm?",
  raw:["To ensure the recommended product is truly the most suitable and competitive option for the client's specific needs.",
       "To show the client how many products the firm owns.",
       "To confuse the client so they choose the most expensive option.",
       "Because CIRO requires every client to receive at least five different options for every trade."],
  exp:"This is part of the 'Best Interest' standard. You shouldn't just pick the first product you see; you should pick the one that is best for the client among the available choices." },

{ id:101, el:7, pos:2, q:"An RR is recommending a 'Leveraged' strategy (borrowing money to invest). What is the most critical risk to explain to the client?",
  raw:["Magnification of losses; if the investment value falls, the client still owes the full loan plus interest, which can lead to losses exceeding the original investment.",
       "The risk that the bank will give the client too much money.",
       "The risk that the government will make the investment tax-free.",
       "The risk that the RR will earn a lower commission."],
  exp:"Leverage is 'double-edged.' While it boosts gains, it turns a 10% market drop into a much larger loss for the client's equity." },

{ id:102, el:7, pos:0, q:"Under CIRO rules, a 'Recommendation' is made when:",
  raw:["The RR provides advice that is intended to influence a client to take a specific investment action (Buy, Sell, or Hold).",
       "The RR simply explains how a TFSA works in general.",
       "The RR provides a list of the top 10 stocks on the TSX without comment.",
       "The client calls to ask for a quote on a stock price."],
  exp:"If the RR 'nudges' the client toward a decision, it is a recommendation and triggers the suitability obligation." },

{ id:103, el:7, pos:3, q:"A client wants to save for their child's university education in 18 years. The RR recommends a portfolio of 80% Equities and 20% Bonds. Is this suitable?",
  raw:["Yes, because the long Time Horizon (18 years) provides the capacity to endure the volatility of equities in exchange for higher growth.",
       "No, because all education savings must be in GICs.",
       "No, because 18 years is considered a 'Short-Term' horizon.",
       "Yes, but only if the RR guarantees a 10% return."],
  exp:"Time horizon is the 'equalizer' for risk. For an 18-year goal, an equity-heavy tilt is appropriate to beat inflation." },

{ id:104, el:7, pos:1, q:"Which of the following must be included in a 'Trade Confirmation' sent to a client after an investment recommendation is executed?",
  raw:["The price, the quantity, the commission charged, and whether the firm acted as agent or principal.",
       "The RR's home address and personal phone number.",
       "A list of all the other stocks the client should buy next month.",
       "A legal guarantee that the stock price will rise."],
  exp:"Confirmations are the 'receipt' for the trade. They provide the essential data points for the client to verify the execution matched their instructions." },

{ id:105, el:7, pos:2, q:"An RR is recommending a mutual fund with a 'Deferred Sales Charge' (DSC). What is the primary restriction on this recommendation for new clients?",
  raw:["DSCs are effectively banned or severely restricted in most Canadian jurisdictions to protect clients from 'lock-in' fees.",
       "DSCs are mandatory for all 'Low Risk' clients.",
       "DSCs are only allowed if the fund manager is the RR's brother.",
       "DSCs are the most tax-efficient way to buy funds."],
  exp:"Regulators have moved away from DSCs because they create a conflict of interest and prevent clients from leaving a bad investment without paying a penalty." },

{ id:106, el:7, pos:0, q:"A client wants 'Income' but is in the highest tax bracket. The RR recommends a 'Return of Capital' (ROC) fund. What is a key feature of ROC?",
  raw:["The cash distributions are not immediately taxable, but they reduce the client's adjusted cost base (ACB), potentially increasing capital gains in the future.",
       "ROC distributions are taxed as interest income.",
       "ROC guarantees that the client will never lose their principal.",
       "ROC is only for clients over the age of 90."],
  exp:"ROC is 'your own money coming back to you.' It's great for cash flow, but clients must understand it's not 'yield' or 'profit'—it's a return of their original investment." },

{ id:107, el:7, pos:3, q:"When an RR recommends a 'Hedge Fund' to a client, which KYC factor is most crucial to verify?",
  raw:["Investment Knowledge and Risk Tolerance, as these products often use complex strategies (shorting, leverage) and are illiquid.",
       "The client's favorite color.",
       "Whether the client has a passport.",
       "The client's occupation, but only if they are a doctor."],
  exp:"Hedge funds are 'Exempt Market' products. They don't have a prospectus and are very risky. The client must be sophisticated enough to understand what they are getting into." },

{ id:108, el:7, pos:1, q:"An RR recommends a 'Diversified' portfolio of 10 different Sector ETFs. This is an example of:",
  raw:["Reducing 'Unsystematic Risk' through broad market exposure.",
       "Eliminating all 'Systematic Risk' from the portfolio.",
       "Avoiding all management fees.",
       "A 'Speculative' strategy."],
  exp:"By holding all sectors (Energy, Tech, Banks, etc.), the RR ensures that the failure of one industry doesn't sink the whole ship." },

// ══ ELEMENT 8 — Q109-115 ══
{ id:109, el:8, pos:2, q:"A client wants to buy 500 shares of ABC Corp, but only if the price is $25.00 or lower. Which order type should the RR use?",
  raw:["Limit Order.",
       "Market Order.",
       "Stop-Loss Order.",
       "All-or-None Order."],
  exp:"A limit order sets a 'ceiling' for buyers. It guarantees the price (or better) but does not guarantee the trade will actually happen (execution)." },

{ id:110, el:8, pos:0, q:"An RR receives a large 'Buy' order from a client. Before entering the client's order, the RR buys the same stock for their own personal account to profit from the expected price rise. This illegal practice is called:",
  raw:["Front-Running.",
       "Insider Trading.",
       "Wash Trading.",
       "Churning."],
  exp:"Front-running is a breach of fiduciary duty. The client's order must always be entered before the RR's personal order if it could affect the price." },

{ id:111, el:8, pos:3, q:"The 'Universal Market Integrity Rules' (UMIR) are designed to ensure:",
  raw:["That all participants on Canadian equity exchanges trade in a fair, transparent, and competitive manner.",
       "That the TSX is the only exchange allowed to operate in Canada.",
       "That all RRs earn the same salary.",
       "That investors never lose money."],
  exp:"UMIR provides the 'rules of the road' for trading. It prevents manipulative practices like 'artificial pricing' or 'spoofing.'" },

{ id:112, el:8, pos:1, q:"A 'Stop-Loss' order is most commonly used by investors to:",
  raw:["Limit potential losses on an existing position by automatically triggering a 'Market Order' to sell if the price falls to a specific level.",
       "Guarantee a 10% profit on every trade.",
       "Buy a stock only when it reaches its all-time high.",
       "Hide their identity from the regulator."],
  exp:"A stop-loss is an 'insurance policy.' It helps a client 'get out' of a falling stock automatically before the loss becomes catastrophic." },

{ id:113, el:8, pos:2, q:"'Insider Trading' involves trading a security based on:",
  raw:["Material, non-public information that, if known, would likely affect the price of the security.",
       "A 'Buy' recommendation from a famous analyst on television.",
       "Information found in a company's public annual report.",
       "A 'gut feeling' after visiting a company's store."],
  exp:"Insider trading is a criminal offense because it gives one party an unfair, 'cheating' advantage over the rest of the public market." },

{ id:114, el:8, pos:0, q:"In Canada, the standard 'Settlement Date' for most stock and bond trades is:",
  raw:["T + 1 (The trade date plus one business day).",
       "T + 3.",
       "The same day (Instant).",
       "One month after the trade."],
  exp:"As of 2024, Canada and the US moved to 'T+1' to reduce systemic risk and speed up the transfer of cash and securities." },

{ id:115, el:8, pos:3, q:"A Registered Representative (RR) has a 'Best Execution' obligation, which means they must:",
  raw:["Seek the most advantageous execution terms reasonably available for a client's order, considering price, speed, and liquidity.",
       "Always trade on the TSX, regardless of the price on other exchanges.",
       "Execute the trade that earns the RR the highest commission.",
       "Delay the trade until the end of the day to see if the price gets better."],
  exp:"Best execution means the RR must work to get the client the 'best deal' possible in a fragmented market with many different exchanges (Alpha, Omega, CX2, etc.)." },

// ══ ELEMENT 9 — Q116-120 ══
{ id:116, el:9, pos:1, q:"How often must a Dealer Member typically send an 'Account Statement' to a client if there has been any trading activity in the account?",
  raw:["Monthly.",
       "Quarterly.",
       "Annually.",
       "Only when the client closes the account."],
  exp:"Statements keep the client informed. If there is activity, monthly is the standard. If there is no activity, quarterly is usually sufficient." },

{ id:117, el:9, pos:2, q:"An RR receives a formal written complaint from a client alleging that a recommendation was unsuitable. What is the RR's immediate obligation?",
  raw:["The RR must immediately report the complaint to their firm's Compliance Department or Branch Manager for investigation.",
       "The RR should delete the email and hope the client forgets.",
       "The RR should offer the client $1,000 cash from their own pocket to drop the complaint.",
       "The RR should call the client and argue with them until they take the complaint back."],
  exp:"Complaints are serious regulatory matters. Firms have a 'Complaint Handling' process that is overseen by CIRO to ensure clients are treated fairly." },

{ id:118, el:9, pos:0, q:"Which of the following is a 'Contemporaneous Note' that an RR should maintain in their client files?",
  raw:["A detailed record of a conversation where an RR explained the risks of a high-risk product to a client before they agreed to the trade.",
       "A list of the RR's favorite vacation spots.",
       "A copy of the company's internal holiday party invite.",
       "A note about what the RR ate for lunch."],
  exp:"If it isn't written down, it didn't happen. Notes are the RR's 'shield' during a compliance audit or a client dispute to prove they fulfilled their suitability and disclosure duties." },

{ id:119, el:9, pos:3, q:"Under CIRO rules, for how many years must a Dealer Member maintain most client records (like KYC forms and trade confirmations)?",
  raw:["7 years.",
       "1 year.",
       "50 years.",
       "Until the RR retires."],
  exp:"Seven years is the standard 'look-back' period for audits, tax purposes, and legal disputes." },

{ id:120, el:9, pos:1, q:"A client calls to say they are moving to the United States permanently. Why is this a 'Significant Change' for the RR?",
  raw:["The RR may no longer be registered to provide advice or trade in the client's new jurisdiction, potentially requiring the account to be closed or transferred.",
       "The RR will have to pay for the long-distance phone calls.",
       "The client will no longer be allowed to own Canadian stocks.",
       "The RR is required to go on vacation with the client."],
  exp:"Registration is 'geographical.' An RR registered only in Ontario cannot typically provide advice to a resident of Florida. This is a major compliance trigger." },
];

// Build final question bank with shuffled options
const ALL_QUESTIONS = RAW.map(({ raw, pos, ...rest }) => {
  const wrongs = [raw[1], raw[2], raw[3]];
  const opts = [...wrongs];
  opts.splice(pos, 0, raw[0]);
  return { ...rest, opts, correct: pos };
});

// ─── UTILITIES ─────────────────────────────────────────────────────────────────
function fmtTime(s) {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return `${h}:${String(m).padStart(2,"0")}:${String(ss).padStart(2,"0")}`;
}

function ScoreRing({ pct, size = 100, stroke = 10, color = "#2563eb" }) {
  const r = (size - stroke) / 2, circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: "stroke-dashoffset 1s ease" }} />
      <text x={size/2} y={size/2 + 6} textAnchor="middle"
        fontSize={size / 5.5} fontWeight={700} fill="#0f172a">{Math.round(pct)}%</text>
    </svg>
  );
}

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function RSEMockExam() {
  const TOTAL_SECS = 3 * 60 * 60;
  const [phase, setPhase]     = useState("cover"); // cover | exam | results
  const [answers, setAnswers] = useState({});       // { qId: chosenIdx }
  const [current, setCurrent] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TOTAL_SECS);
  const [flagged, setFlagged] = useState(new Set());
  const [reviewEl, setReviewEl] = useState(null);
  const [showAll,  setShowAll]  = useState(false);
  const timerRef = useRef(null);
  const qLen = ALL_QUESTIONS.length;

  useEffect(() => {
    if (phase === "exam") {
      timerRef.current = setInterval(() => setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); setPhase("results"); return 0; }
        return t - 1;
      }), 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [phase]);

  const answered = Object.keys(answers).length;

  function submit() {
    if (answered < qLen && !window.confirm(`You have ${qLen - answered} unanswered questions. Submit anyway?`)) return;
    clearInterval(timerRef.current);
    setPhase("results");
  }

  function resetExam() {
    setAnswers({}); setFlagged(new Set()); setCurrent(0);
    setTimeLeft(TOTAL_SECS); setReviewEl(null); setShowAll(false);
    setPhase("cover");
  }

  // Diagnostics
  const elStats = ELEMENTS.map(el => {
    const qs = ALL_QUESTIONS.filter(q => q.el === el.id);
    const correct = qs.filter(q => answers[q.id] === q.correct).length;
    return { ...el, qs, correct, total: qs.length, pct: qs.length ? Math.round(correct / qs.length * 100) : 0 };
  });
  const totalCorrect = ALL_QUESTIONS.filter(q => answers[q.id] === q.correct).length;
  const totalPct = Math.round(totalCorrect / qLen * 100);
  const passScore = Math.ceil(qLen * 0.70);
  const passed = totalCorrect >= passScore;
  const missedAll = ALL_QUESTIONS.filter(q => answers[q.id] !== q.correct);
  const missedForEl = reviewEl ? missedAll.filter(q => q.el === reviewEl) : [];

  const q = ALL_QUESTIONS[current];

  // ══ COVER ══════════════════════════════════════════════════════════════════
  if (phase === "cover") return (
    <div style={{ fontFamily:"system-ui,sans-serif", background:"#f8fafc", minHeight:"100vh",
      display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ maxWidth:640, width:"100%", background:"#fff", borderRadius:16,
        border:"1px solid #e2e8f0", padding:"36px 44px", boxShadow:"0 4px 24px rgba(0,0,0,0.06)" }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ fontSize:44, marginBottom:10 }}>📋</div>
          <h1 style={{ fontFamily:"Georgia,serif", fontSize:28, color:"#0f172a", margin:"0 0 6px" }}>
            RSE Full Mock Exam
          </h1>
          <p style={{ color:"#475569", fontSize:14, margin:0 }}>Retail Securities Exam — Full Simulation</p>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:24 }}>
          {[["📝","120 Questions","Multiple choice"],["⏱️","3 Hours","Timed exam"],["🎯","70%","Passing grade (84/120)"]].map(([ic,t,d]) => (
            <div key={t} style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:10,
              padding:"14px 10px", textAlign:"center" }}>
              <div style={{ fontSize:22, marginBottom:4 }}>{ic}</div>
              <div style={{ fontWeight:700, fontSize:14, color:"#0f172a" }}>{t}</div>
              <div style={{ fontSize:11, color:"#94a3b8", marginTop:2 }}>{d}</div>
            </div>
          ))}
        </div>
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginBottom:10,
            textTransform:"uppercase", letterSpacing:"0.06em" }}>Element Distribution</div>
          <div style={{ display:"grid", gap:5 }}>
            {ELEMENTS.map((el, i) => (
              <div key={el.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                padding:"6px 12px", borderRadius:8, background:"#f8fafc", border:"1px solid #e2e8f0" }}>
                <span style={{ fontSize:12, color:"#0f172a" }}>{el.icon} {el.label}: {el.title}</span>
                <span style={{ fontSize:12, fontWeight:700, padding:"2px 10px", borderRadius:12,
                  background:`${EL_COLORS[i]}18`, color:EL_COLORS[i] }}>
                  {ALL_QUESTIONS.filter(q => q.el === el.id).length} Qs
                </span>
              </div>
            ))}
          </div>
        </div>
        <button onClick={() => setPhase("exam")}
          style={{ width:"100%", padding:14, background:"#2563eb", color:"#fff", border:"none",
            borderRadius:10, fontSize:15, fontWeight:700, cursor:"pointer",
            boxShadow:"0 2px 12px rgba(37,99,235,0.3)" }}>
          Begin Exam →
        </button>
        <p style={{ textAlign:"center", color:"#94a3b8", fontSize:11, marginTop:12, marginBottom:0 }}>
          Correct answers are randomized across options A, B, C, and D
        </p>
      </div>
    </div>
  );

  // ══ EXAM ═══════════════════════════════════════════════════════════════════
  if (phase === "exam") return (
    <div style={{ fontFamily:"system-ui,sans-serif", background:"#f8fafc", minHeight:"100vh" }}>
      {/* Sticky header */}
      <div style={{ position:"sticky", top:0, zIndex:100, background:"#fff",
        borderBottom:"1px solid #e2e8f0", padding:"10px 20px" }}>
        <div style={{ maxWidth:920, margin:"0 auto" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
              <span style={{ fontFamily:"Georgia,serif", fontWeight:700, fontSize:15, color:"#0f172a" }}>
                RSE Mock Exam
              </span>
              <span style={{ fontSize:12, background:"#f1f5f9", color:"#475569", padding:"3px 10px", borderRadius:20 }}>
                Q {current + 1}/{qLen}
              </span>
              <span style={{ fontSize:12, background:"#dbeafe", color:"#1e40af", padding:"3px 10px", borderRadius:20, fontWeight:600 }}>
                {answered}/{qLen} answered
              </span>
              {flagged.size > 0 && (
                <span style={{ fontSize:12, background:"#fef3c7", color:"#92400e", padding:"3px 10px", borderRadius:20, fontWeight:600 }}>
                  🚩 {flagged.size} flagged
                </span>
              )}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ fontFamily:"monospace", fontSize:16, fontWeight:700,
                color:timeLeft < 1800 ? "#dc2626" : "#0f172a",
                background:timeLeft < 1800 ? "#fee2e2" : "#f1f5f9",
                padding:"5px 14px", borderRadius:8 }}>
                ⏱ {fmtTime(timeLeft)}
              </div>
              <button onClick={submit}
                style={{ padding:"7px 16px", background:"#16a34a", color:"#fff", border:"none",
                  borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13 }}>
                Submit
              </button>
            </div>
          </div>
          <div style={{ height:4, background:"#e2e8f0", borderRadius:2 }}>
            <div style={{ height:"100%", background:"#2563eb", borderRadius:2,
              width:`${(answered / qLen) * 100}%`, transition:"width 0.3s" }} />
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth:920, margin:"0 auto", padding:"24px 16px",
        display:"grid", gridTemplateColumns:"1fr 196px", gap:18 }}>

        {/* Question panel */}
        <div>
          <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:12,
            padding:"24px 28px", marginBottom:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.07em", textTransform:"uppercase",
                color:EL_COLORS[q.el - 1], background:`${EL_COLORS[q.el - 1]}18`, padding:"3px 12px", borderRadius:12 }}>
                {ELEMENTS[q.el - 1].icon} {ELEMENTS[q.el - 1].title}
              </span>
              <button onClick={() => setFlagged(f => { const s = new Set(f); s.has(q.id) ? s.delete(q.id) : s.add(q.id); return s; })}
                style={{ background:"none", border:"none", cursor:"pointer", fontSize:20,
                  color:flagged.has(q.id) ? "#f59e0b" : "#cbd5e1" }}
                title={flagged.has(q.id) ? "Unflag" : "Flag for review"}>
                {flagged.has(q.id) ? "🚩" : "⚑"}
              </button>
            </div>
            <p style={{ fontFamily:"Georgia,serif", fontSize:15, lineHeight:1.8,
              color:"#0f172a", margin:"0 0 24px", fontWeight:600 }}>{q.q}</p>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {q.opts.map((opt, i) => {
                const sel = answers[q.id] === i;
                return (
                  <button key={i} onClick={() => setAnswers(a => ({ ...a, [q.id]: i }))}
                    style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"13px 16px",
                      borderRadius:9, border:`2px solid ${sel ? "#2563eb" : "#e2e8f0"}`,
                      background:sel ? "#eff6ff" : "#fafafa", cursor:"pointer", textAlign:"left",
                      transition:"all 0.15s" }}>
                    <span style={{ flexShrink:0, width:26, height:26, borderRadius:"50%",
                      background:sel ? "#2563eb" : "#e2e8f0", color:sel ? "#fff" : "#64748b",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontWeight:700, fontSize:12, marginTop:1 }}>
                      {["A","B","C","D"][i]}
                    </span>
                    <span style={{ fontSize:13, lineHeight:1.65, color:"#0f172a" }}>{opt}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            <button disabled={current === 0} onClick={() => setCurrent(c => c - 1)}
              style={{ padding:"9px 20px", borderRadius:8, border:"1px solid #e2e8f0",
                background:current === 0 ? "#f8fafc" : "#fff",
                cursor:current === 0 ? "not-allowed" : "pointer",
                color:current === 0 ? "#cbd5e1" : "#475569", fontWeight:600, fontSize:13 }}>
              ← Prev
            </button>
            <button disabled={current === qLen - 1} onClick={() => setCurrent(c => c + 1)}
              style={{ padding:"9px 20px", borderRadius:8, border:"none",
                background:current === qLen - 1 ? "#e2e8f0" : "#2563eb",
                cursor:current === qLen - 1 ? "not-allowed" : "pointer",
                color:current === qLen - 1 ? "#94a3b8" : "#fff", fontWeight:700, fontSize:13 }}>
              Next →
            </button>
            {flagged.size > 0 && (
              <button onClick={() => {
                const fl = [...flagged].sort((a, b) => a - b);
                const nextId = fl.find(id => id > q.id) ?? fl[0];
                setCurrent(ALL_QUESTIONS.findIndex(q => q.id === nextId));
              }} style={{ padding:"9px 16px", borderRadius:8, border:"1.5px solid #f59e0b",
                background:"#fef3c7", color:"#92400e", cursor:"pointer", fontWeight:700, fontSize:13 }}>
                Next 🚩 Flagged
              </button>
            )}
          </div>
        </div>

        {/* Q-grid sidebar */}
        <div style={{ position:"sticky", top:68, alignSelf:"start" }}>
          <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:12,
            padding:"12px 10px", maxHeight:"82vh", overflowY:"auto" }}>
            <div style={{ fontSize:10, fontWeight:700, color:"#94a3b8", marginBottom:8,
              textTransform:"uppercase", letterSpacing:"0.06em" }}>Question Map</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:3 }}>
              {ALL_QUESTIONS.map((qq, i) => {
                const isAns = answers[qq.id] !== undefined;
                const isFlag = flagged.has(qq.id);
                const isCur = i === current;
                return (
                  <button key={qq.id} onClick={() => setCurrent(i)}
                    style={{ width:28, height:28, borderRadius:5, fontSize:9, fontWeight:700, cursor:"pointer",
                      border:`2px solid ${isCur ? "#2563eb" : isFlag ? "#f59e0b" : isAns ? "#16a34a" : "#e2e8f0"}`,
                      background:isCur ? "#2563eb" : isFlag ? "#fef3c7" : isAns ? "#dcfce7" : "#f8fafc",
                      color:isCur ? "#fff" : isFlag ? "#92400e" : isAns ? "#166534" : "#94a3b8" }}>
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop:10, display:"grid", gap:4 }}>
              {[["#dcfce7","#86efac","✓ Answered"],["#fef3c7","#fcd34d","🚩 Flagged"],["#f8fafc","#e2e8f0","— Unanswered"]].map(([bg, bc, l]) => (
                <div key={l} style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ width:11, height:11, borderRadius:3, background:bg, border:`1px solid ${bc}` }} />
                  <span style={{ fontSize:10, color:"#94a3b8" }}>{l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ══ RESULTS ═════════════════════════════════════════════════════════════════
  return (
    <div style={{ fontFamily:"system-ui,sans-serif", background:"#f8fafc", minHeight:"100vh", padding:"20px 16px" }}>
      <div style={{ maxWidth:900, margin:"0 auto" }}>

        {/* Score card */}
        <div style={{ background:passed?"linear-gradient(135deg,#f0fdf4,#fff)":"linear-gradient(135deg,#fef2f2,#fff)",
          border:`2px solid ${passed ? "#86efac" : "#fca5a5"}`,
          borderRadius:16, padding:"28px 36px", marginBottom:20 }}>
          <div style={{ display:"flex", alignItems:"center", gap:28, flexWrap:"wrap" }}>
            <ScoreRing pct={totalPct} size={110} stroke={11}
              color={passed ? "#16a34a" : totalPct >= 50 ? "#d97706" : "#dc2626"} />
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:"Georgia,serif", fontSize:24, fontWeight:700, color:"#0f172a", marginBottom:4 }}>
                {passed ? "✅ Passed" : "❌ Did Not Pass"}
              </div>
              <div style={{ fontSize:14, color:"#475569", marginBottom:14 }}>
                {totalCorrect}/{qLen} correct · {totalPct}% · Pass mark: 60% ({passScore}/{qLen})
              </div>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                {[["✓ Correct",totalCorrect,"#166534","#dcfce7","#86efac"],
                  ["✗ Incorrect",qLen - totalCorrect,"#dc2626","#fee2e2","#fca5a5"],
                  ["— Unanswered",qLen - answered,"#475569","#f1f5f9","#e2e8f0"]].map(([l,v,tc,bg,bc]) => (
                  <div key={l} style={{ padding:"8px 16px", borderRadius:10, background:bg, border:`1px solid ${bc}` }}>
                    <div style={{ fontSize:10, color:"#94a3b8", marginBottom:2 }}>{l}</div>
                    <div style={{ fontWeight:700, fontSize:20, color:tc }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={resetExam}
              style={{ alignSelf:"flex-start", padding:"9px 18px", background:"#2563eb", color:"#fff",
                border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13 }}>
              Retake
            </button>
          </div>
        </div>

        {/* Diagnostic by element */}
        <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16,
          padding:"24px 28px", marginBottom:20 }}>
          <div style={{ fontFamily:"Georgia,serif", fontSize:17, fontWeight:700,
            color:"#0f172a", marginBottom:20 }}>📊 Diagnostic Report — Performance by Element</div>
          <div style={{ display:"grid", gap:16 }}>
            {elStats.map((el, i) => {
              const elMissed = missedAll.filter(q => q.el === el.id);
              const isOpen = reviewEl === el.id;
              return (
                <div key={el.id}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:7 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:18 }}>{el.icon}</span>
                      <span style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>{el.label}: {el.title}</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                      <span style={{ fontSize:12, color:"#475569" }}>{el.correct}/{el.total}</span>
                      <span style={{ fontSize:12, fontWeight:700, padding:"3px 12px", borderRadius:12,
                        background:el.pct >= 70 ? `${EL_COLORS[i]}18` : "#fee2e2",
                        color:el.pct >= 70 ? EL_COLORS[i] : "#dc2626" }}>{el.pct}%</span>
                      {elMissed.length > 0 && (
                        <button onClick={() => setReviewEl(isOpen ? null : el.id)}
                          style={{ fontSize:11, padding:"3px 12px", borderRadius:12,
                            border:`1px solid ${EL_COLORS[i]}`, background:"none",
                            cursor:"pointer", color:EL_COLORS[i], fontWeight:600 }}>
                          {isOpen ? "▲ Hide" : `▼ Review ${elMissed.length} missed`}
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ height:9, background:"#f1f5f9", borderRadius:5 }}>
                    <div style={{ height:"100%", borderRadius:5,
                      background:el.pct >= 70 ? EL_COLORS[i] : "#dc2626",
                      width:`${el.pct}%`, transition:"width 0.9s ease" }} />
                  </div>

                  {/* Per-element missed questions */}
                  {isOpen && missedForEl.map(mq => (
                    <MissedCard key={mq.id} mq={mq} ua={answers[mq.id]} elIdx={mq.el - 1} />
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Weak areas */}
        {elStats.filter(e => e.pct < 70).length > 0 && (
          <div style={{ background:"#fffbeb", border:"1px solid #fcd34d", borderRadius:16,
            padding:"20px 24px", marginBottom:20 }}>
            <div style={{ fontFamily:"Georgia,serif", fontSize:15, fontWeight:700,
              color:"#92400e", marginBottom:12 }}>⚠️ Priority Study Areas (below 70%)</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(230px,1fr))", gap:10 }}>
              {elStats.filter(e => e.pct < 70).sort((a,b) => a.pct - b.pct).map(el => (
                <div key={el.id} style={{ padding:"10px 14px", background:"#fff", borderRadius:8,
                  border:`1px solid ${EL_COLORS[el.id - 1]}40` }}>
                  <div style={{ fontWeight:700, fontSize:12, color:EL_COLORS[el.id - 1], marginBottom:2 }}>
                    {el.icon} {el.label}
                  </div>
                  <div style={{ fontSize:12, color:"#475569", marginBottom:4 }}>{el.title}</div>
                  <div style={{ fontSize:14, fontWeight:700, color:"#dc2626" }}>
                    {el.correct}/{el.total} correct ({el.pct}%)
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All missed */}
        <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, padding:"20px 24px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontFamily:"Georgia,serif", fontSize:17, fontWeight:700, color:"#0f172a" }}>
              📖 All Missed Questions ({missedAll.length})
            </div>
            <button onClick={() => setShowAll(v => !v)}
              style={{ padding:"8px 18px", background:showAll ? "#dc2626" : "#2563eb",
                color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:13 }}>
              {showAll ? "▲ Collapse" : `▼ Show All ${missedAll.length}`}
            </button>
          </div>
          {showAll && (
            missedAll.length === 0
              ? <div style={{ textAlign:"center", padding:32 }}>
                  <div style={{ fontSize:40, marginBottom:8 }}>🏆</div>
                  <div style={{ fontFamily:"Georgia,serif", fontSize:18, color:"#0f172a" }}>Perfect score!</div>
                </div>
              : missedAll.map(mq => (
                  <MissedCard key={mq.id} mq={mq} ua={answers[mq.id]} elIdx={mq.el - 1} showEl />
                ))
          )}
        </div>

      </div>
    </div>
  );
}

// ─── MISSED CARD ───────────────────────────────────────────────────────────────
function MissedCard({ mq, ua, elIdx, showEl }) {
  return (
    <div style={{ marginTop:12, padding:"16px 18px", borderRadius:10,
      background:"#fef2f2", border:"1.5px solid #fca5a5" }}>
      {showEl && (
        <div style={{ display:"flex", gap:8, marginBottom:10 }}>
          <span style={{ fontSize:11, fontWeight:700, padding:"2px 10px", borderRadius:12,
            background:`${EL_COLORS[elIdx]}20`, color:EL_COLORS[elIdx] }}>
            {ELEMENTS[elIdx].icon} {ELEMENTS[elIdx].label}
          </span>
          <span style={{ fontSize:11, color:"#94a3b8" }}>Q{mq.id}</span>
        </div>
      )}
      <p style={{ margin:"0 0 10px", fontSize:13, fontWeight:600, color:"#0f172a", lineHeight:1.65 }}>
        {showEl ? "" : <span style={{ color:"#94a3b8", fontSize:11, marginRight:6 }}>Q{mq.id}.</span>}
        {mq.q}
      </p>
      <div style={{ display:"grid", gap:6, marginBottom:10 }}>
        {mq.opts.map((opt, i) => {
          const isCorrect = i === mq.correct, isYours = i === ua;
          if (!isCorrect && !isYours) return null;
          return (
            <div key={i} style={{ display:"flex", gap:8, padding:"8px 12px", borderRadius:7,
              background:isCorrect ? "#dcfce7" : "#fee2e2",
              border:`1px solid ${isCorrect ? "#86efac" : "#fca5a5"}` }}>
              <span style={{ fontWeight:700, fontSize:12, flexShrink:0,
                color:isCorrect ? "#166534" : "#dc2626" }}>
                {isCorrect ? "✓" : "✗"} {["A","B","C","D"][i]}.
              </span>
              <span style={{ fontSize:12, color:"#0f172a", flex:1 }}>{opt}</span>
              {isYours && !isCorrect && (
                <span style={{ fontSize:11, color:"#dc2626", flexShrink:0, fontStyle:"italic" }}>← your answer</span>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ padding:"10px 14px", background:"#fff", borderRadius:8, border:"1px solid #e2e8f0" }}>
        <span style={{ fontSize:11, fontWeight:700, color:"#2563eb" }}>Explanation: </span>
        <span style={{ fontSize:12, color:"#475569", lineHeight:1.6 }}>{mq.exp}</span>
      </div>
    </div>
  );
}
